import { useEffect, useMemo, useState } from "react";
import {

  approveGroupJoinRequest,
  createGroupActivity,
  createGroupInvite,
  createGroupTodo,
  getGroup,
  listGroupActivities,
  listGroupInvites,
  listGroupJoinRequests,
  listGroupMembers,
  listGroupTodos,
  joinGroupActivity,
  leaveGroup,
  leaveGroupActivity,
  rejectGroupJoinRequest,
  updateGroupPrivacy,
  updateGroupPostingPolicy,
  updateMyGroupMemberPrivacy,
  reportGroup,
  reportUser,
  setGroupMemberRole,
  kickGroupMember,
  banGroupMember,
  requestJoinGroup,
  revokeGroupInvite,
} from "../api/groups";

function memberLabel(member: any): string {
  return String(
    member?.display_name ||
    member?.displayName ||
    member?.name ||
    member?.username ||
    member?.user_public_id ||
    member?.public_id ||
    member?.user_id ||
    "Member"
  );
}


function roleLabel(role: string | null | undefined) {
  if (!role) return "unknown";
  if (role === "owner") return "Owner";
  if (role === "admin") return "Admin";
  if (role === "moderator") return "Moderator";
  if (role === "member") return "Member";
  if (role === "none") return "none";
  return role;
}

function isAdminRole(role: string | null | undefined) {
  return role === "owner" || role === "admin";
}

function isModeratorRole(role: string | null | undefined) {
  return role === "owner" || role === "admin" || role === "moderator";
}

function roleRank(role: string | null | undefined) {
  if (role === "owner") return 50;
  if (role === "admin") return 40;
  if (role === "moderator") return 30;
  if (role === "voice") return 20;
  if (role === "member") return 10;
  return 0;
}

function canModerateTarget(actorRole: string | null | undefined, targetRole: string | null | undefined) {
  return roleRank(actorRole) >= 30 && roleRank(actorRole) > roleRank(targetRole);
}

function canChangeRoles(actorRole: string | null | undefined) {
  return roleRank(actorRole) >= 40;
}

function postingPolicyLabel(v: any) {
  const p = String(v || "members");
  if (p === "voiced_only") return "Voiced only";
  if (p === "moderators_only") return "Moderators only";
  if (p === "admins_only") return "Admins only";
  if (p === "announcements") return "Announcements";
  return "All members";
}

function isMemberLike(role: string | null | undefined) {
  return role === "owner" || role === "admin" || role === "moderator" || role === "member";
}

function itemArray(res: any): any[] {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.items)) return res.items;
  if (Array.isArray(res?.rows)) return res.rows;
  return [];
}

function currentUserIdentity(): { uid: string | null; sub: string | null } {
  const token = window.localStorage.getItem("intimsync.access_token");
  if (!token) return { uid: null, sub: null };

  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return {
      uid: payload?.uid ? String(payload.uid) : null,
      sub: payload?.sub ? String(payload.sub) : null,
    };
  } catch {
    return { uid: null, sub: null };
  }
}

function deriveViewerRole(item: any, members: any[]): string {
  if (item?.viewerRole) return String(item.viewerRole);

  const ident = currentUserIdentity();

  const row = members.find((m) => {
    const publicId = m.public_id || m.uid || m.user_uid;
    const userId = m.user_id || m.id || m.member_user_id;

    return (
      (ident.uid && publicId && String(publicId) === String(ident.uid)) ||
      (ident.sub && userId && String(userId) === String(ident.sub))
    );
  });

  return String(row?.role_name || item?.viewerMembership || "none");
}

function displayMemberLabel(m: any) {
  return (
    m.displayName ||
    m.display_name ||
    m.name ||
    m.username ||
    m.handle ||
    m.email ||
    m.display_label ||
    m.public_id ||
    m.uid ||
    m.user_id ||
    "member"
  );
}

function displayRequestUser(r: any) {
  return (
    r.displayName ||
    r.display_name ||
    r.display_label ||
    r.name ||
    r.username ||
    r.email ||
    r.public_id ||
    r.uid ||
    r.user_id ||
    r.requester_user_id ||
    "user"
  );
}


function formatActivityDate(value: any) {
  if (!value) return "No date set";

  const raw = String(value);
  const normalized = raw.replace("T", " ");
  const datePart = normalized.slice(0, 10);
  const timePart = normalized.slice(11, 16);

  if (
    datePart.length === 10 &&
    timePart.length === 5 &&
    datePart.includes("-") &&
    timePart.includes(":")
  ) {
    const [yyyy, mm, dd] = datePart.split("-");
    return `${dd}-${mm}-${yyyy} ${timePart}`;
  }

  return raw;
}

function activityStatusLabel(status: any) {
  const raw = String(status || "planned");

  if (raw === "active") return "planned";
  if (raw === "planned") return "planned";
  if (raw === "cancelled") return "cancelled";
  if (raw === "done") return "done";

  return raw;
}

function currentUserIdFromToken() {
  try {
    const token = window.localStorage.getItem("intimsync.access_token");
    if (!token) return "";
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload?.sub ? String(payload.sub) : "";
  } catch {
    return "";
  }
}

type GroupDetailTab = "overview" | "activities" | "todos" | "calendar" | "members" | "settings";

function readInitialGroupTab(): GroupDetailTab {
  try {
    const raw = new URLSearchParams(window.location.search).get("tab") || "overview";
    if (
      raw === "activities" ||
      raw === "todos" ||
      raw === "calendar" ||
      raw === "members" ||
      raw === "settings"
    ) {
      return raw;
    }
  } catch {}
  return "overview";
}

function boolish(value: any, fallback = false) {
  if (value === true || value === 1 || value === "1") return true;
  if (value === false || value === 0 || value === "0") return false;
  return fallback;
}

export default function GroupDetailPage() {
  const groupId = useMemo(() => {
    const parts = window.location.pathname.split("/").filter(Boolean);
    return decodeURIComponent(parts[parts.length - 1] || "");
  }, []);

  const [item, setItem] = useState<any | null>(null);
  const currentUserId = useMemo(() => currentUserIdFromToken(), []);
  const [activeTab, setActiveTab] = useState<GroupDetailTab>(() => readInitialGroupTab());
  const [message, setMessage] = useState("");
  const [statusMsg, setStatusMsg] = useState<string | null>(null);

  const [members, setMembers] = useState<any[]>([]);
  const [invites, setInvites] = useState<any[]>([]);
  const [todos, setTodos] = useState<any[]>([]);
  const [activities, setActivities] = useState<any[]>([]);
  const [joinRequests, setJoinRequests] = useState<any[]>([]);

  const [todoTitle, setTodoTitle] = useState("");
  const [todoDescription, setTodoDescription] = useState("");
  const [todoDueAt, setTodoDueAt] = useState("");
  const [todoLocation, setTodoLocation] = useState("");
  const [todoAssignedIds, setTodoAssignedIds] = useState<string[]>([]);

  const [activityTitle, setActivityTitle] = useState("");
  const [activityDescription, setActivityDescription] = useState("");
  const [activityLocation, setActivityLocation] = useState("");
  const [activityStartsAt, setActivityStartsAt] = useState("");

  const [inviteEmail, setInviteEmail] = useState("");
  const [createdInvite, setCreatedInvite] = useState<any | null>(null);

  const [loading, setLoading] = useState(false);
  const [managementBusy, setManagementBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const viewerRole = deriveViewerRole(item, members);
  const viewerIsMember = isMemberLike(viewerRole);
  const viewerIsAdmin = isAdminRole(viewerRole);
  const viewerIsModerator = isModeratorRole(viewerRole);

  async function loadManagementPanels(nextItem: any) {
    const baseRole = String(nextItem?.viewerRole || nextItem?.viewerMembership || "none");

    if (!isMemberLike(baseRole)) {
      setMembers([]);
      setInvites([]);
      setTodos([]);
      setActivities([]);
      setJoinRequests([]);
      return;
    }

    const [membersRes, todosRes, activitiesRes] = await Promise.allSettled([
      listGroupMembers(groupId),
      listGroupTodos(groupId),
      listGroupActivities(groupId),
    ]);

    const nextMembers =
      membersRes.status === "fulfilled" ? itemArray(membersRes.value) : [];

    if (membersRes.status === "fulfilled") {
      setMembers(nextMembers);
    }

    if (todosRes.status === "fulfilled") {
      setTodos(itemArray(todosRes.value));
    }

    if (activitiesRes.status === "fulfilled") {
      setActivities(itemArray(activitiesRes.value));
    }

    const derivedRole = deriveViewerRole(nextItem, nextMembers);

    if (isModeratorRole(derivedRole)) {
      const requestsRes = await Promise.allSettled([listGroupJoinRequests(groupId)]);
      const first = requestsRes[0];
      if (first.status === "fulfilled") {
        setJoinRequests(itemArray(first.value).filter((r) => (r.status || "pending") === "pending"));
      }
    } else {
      setJoinRequests([]);
    }

    if (isAdminRole(derivedRole)) {
      const invitesRes = await Promise.allSettled([listGroupInvites(groupId)]);
      const first = invitesRes[0];
      if (first.status === "fulfilled") {
        setInvites(itemArray(first.value));
      }
    } else {
      setInvites([]);
    }
  }

  async function load() {
    if (!groupId) return;

    setLoading(true);
    setError(null);

    try {
      const res = await getGroup(groupId);
      const nextItem = res?.item || res?.group || res || null;
      setItem(nextItem);

      if (nextItem) {
        await loadManagementPanels(nextItem);
      }
    } catch (e: any) {
      setItem(null);
      setMembers([]);
      setInvites([]);
      setTodos([]);
      setActivities([]);
      setJoinRequests([]);
      setError(e?.message || "Could not load group.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (groupId) {
      try {
        window.localStorage.setItem("intimsync.last_opened_group_id", groupId);
      } catch {
        // ignore
      }
    }

    void load();
  }, [groupId]);

  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("tab", activeTab);
      window.history.replaceState(null, "", url.toString());
    } catch {
      const activeTabUrl = activeTab;
      void activeTabUrl;
    }
  }, [activeTab]);

  useEffect(() => {
    if (!groupId || !viewerIsModerator) return;

    const timer = window.setInterval(() => {
      void refreshJoinRequestsOnly();
    }, 60000);

    return () => window.clearInterval(timer);
  }, [groupId, viewerIsModerator]);

  async function handleLeaveGroup() {
    if (!groupId) return;

    const ok = window.confirm("Leave this group? You may need to request access again later.");
    if (!ok) return;

    setActionBusy(true);
    setStatusMsg(null);

    try {
      await leaveGroup(groupId);
      setStatusMsg("You left the group.");
      window.location.href = "/groups";
    } catch (e: any) {
      setStatusMsg(e?.message || "Could not leave group.");
    } finally {
      setActionBusy(false);
    }
  }

  async function handleRequestJoin() {
    if (!groupId) return;

    setActionBusy(true);
    setStatusMsg(null);

    try {
      const res = await requestJoinGroup(groupId, message);

      const requestStatus =
        res?.status ||
        res?.item?.status ||
        res?.request?.status ||
        res?.joinRequest?.status ||
        "pending";

      if (res?.joined) {
        setStatusMsg("Joined successfully.");
      } else {
        setStatusMsg(
          requestStatus === "pending"
            ? "Join request submitted."
            : `Join request status: ${requestStatus}`
        );
      }

      setMessage("");
      await load();
    } catch (e: any) {
      const msg = String(e?.message || "");

      if (msg === "already_member") {
        setStatusMsg("You are already a member of this group.");
      } else if (msg === "request_already_pending" || msg === "join_request_already_pending") {
        setStatusMsg("You already have a pending join request.");
      } else if (msg === "invite_only") {
        setStatusMsg("This group is invite only.");
      } else {
        setStatusMsg(msg || "Could not request to join.");
      }
    } finally {
      setActionBusy(false);
    }
  }

  function toggleTodoAssignedId(userId: string) {
    if (!userId) return;
    setTodoAssignedIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  }

  function todoAssigneeLabel(todo: any) {
    const assignees = Array.isArray(todo?.assignees) ? todo.assignees.filter(Boolean) : [];
    if (assignees.length) {
      return assignees
        .map((a: any) => a.displayName || a.display_name || a.publicId || a.public_id || a.userId || a.user_id)
        .filter(Boolean)
        .join(", " );
    }

    if (todo?.assigned_user_id) {
      const member = members.find((m) => String(m.user_id || "") === String(todo.assigned_user_id));
      return member ? memberLabel(member) : `User ${todo.assigned_user_id}`;
    }

    return "Unassigned";
  }

  async function handleCreateTodo() {
    const title = todoTitle.trim();
    if (!groupId || !title) return;

    setManagementBusy(true);
    setStatusMsg(null);

    try {
      await createGroupTodo(groupId, {
        title,
        description: todoDescription.trim() || undefined,
        dueAt: todoDueAt || null,
        location: todoLocation.trim() || undefined,
        assignedUserIds: todoAssignedIds,
      });

      setTodoTitle("");
      setTodoDescription("");
      setTodoDueAt("");
      setTodoLocation("");
      setTodoAssignedIds([]);
      setStatusMsg("Todo added.");
      await load();
    } catch (e: any) {
      setStatusMsg(e?.message || "Could not create todo.");
    } finally {
      setManagementBusy(false);
    }
  }

  async function handleCreateActivity() {
    const title = activityTitle.trim();
    if (!groupId || !title) return;

    setManagementBusy(true);
    setStatusMsg(null);

    try {
      await createGroupActivity(groupId, {
        title,
        description: activityDescription.trim() || undefined,
        location: activityLocation.trim() || undefined,
        startsAt: activityStartsAt || null,
      });

      setActivityTitle("");
      setActivityDescription("");
      setActivityLocation("");
      setActivityStartsAt("");
      setStatusMsg("Activity created.");
      await load();
    } catch (e: any) {
      setStatusMsg(e?.message || "Could not create activity.");
    } finally {
      setManagementBusy(false);
    }
  }

  async function handleJoinActivity(activityId: string) {
    if (!groupId || !activityId) return;

    setManagementBusy(true);
    setStatusMsg(null);

    try {
      await joinGroupActivity(groupId, activityId);
      setStatusMsg("Joined activity.");
      await load();
    } catch (e: any) {
      setStatusMsg(e?.message || "Could not join activity.");
    } finally {
      setManagementBusy(false);
    }
  }

  async function handleLeaveActivity(activityId: string) {
    if (!groupId || !activityId) return;

    setManagementBusy(true);
    setStatusMsg(null);

    try {
      await leaveGroupActivity(groupId, activityId);
      setStatusMsg("Left activity.");
      await load();
    } catch (e: any) {
      setStatusMsg(e?.message || "Could not leave activity.");
    } finally {
      setManagementBusy(false);
    }
  }

  async function handleCreateInvite() {
    const email = inviteEmail.trim();
    if (!groupId || !email) return;

    setManagementBusy(true);
    setStatusMsg(null);

    try {
      const res = await createGroupInvite(groupId, { invitedEmail: email });
      setCreatedInvite(res || null);
      setInviteEmail("");
      setStatusMsg("Invite created.");
      await load();
    } catch (e: any) {
      setStatusMsg(e?.message || "Could not create invite.");
    } finally {
      setManagementBusy(false);
    }
  }

  async function handleRevokeInvite(inviteId: string) {
    if (!groupId || !inviteId) return;

    setManagementBusy(true);
    setStatusMsg(null);

    try {
      await revokeGroupInvite(groupId, inviteId);
      setStatusMsg("Invite revoked.");
      await load();
    } catch (e: any) {
      setStatusMsg(e?.message || "Could not revoke invite.");
    } finally {
      setManagementBusy(false);
    }
  }

  async function refreshJoinRequestsOnly() {
    if (!groupId || !viewerIsModerator) return;

    try {
      const res = await listGroupJoinRequests(groupId);
      setJoinRequests(
        itemArray(res).filter((r) => (r.status || "pending") === "pending")
      );
    } catch {
      // Keep existing UI state. Full load() handles visible errors.
    }
  }

  async function handleApproveJoinRequest(requestId: string) {
    if (!groupId || !requestId) return;

    setManagementBusy(true);
    setStatusMsg(null);

    try {
      await approveGroupJoinRequest(groupId, requestId);
      setStatusMsg("Join request approved.");
      await load();
    } catch (e: any) {
      setStatusMsg(e?.message || "Could not approve join request.");
    } finally {
      setManagementBusy(false);
    }
  }

  async function handleRejectJoinRequest(requestId: string) {
    if (!groupId || !requestId) return;

    setManagementBusy(true);
    setStatusMsg(null);

    try {
      await rejectGroupJoinRequest(groupId, requestId);
      setStatusMsg("Join request rejected.");
      await load();
    } catch (e: any) {
      setStatusMsg(e?.message || "Could not reject join request.");
    } finally {
      setManagementBusy(false);
    }
  }

  async function handleReportGroup() {
    if (!groupId) return;

    const details = window.prompt("Why are you reporting this group? Optional details:") || "";
    if (details === null) return;

    setManagementBusy(true);
    setStatusMsg(null);

    try {
      await reportGroup(groupId, {
        reason: "user_report",
        details,
      });
      setStatusMsg("Group report submitted.");
    } catch (e: any) {
      setStatusMsg(e?.message || "Could not report group.");
    } finally {
      setManagementBusy(false);
    }
  }

  async function handleReportUser(userId: string | number) {
    if (!groupId || !userId) return;

    const details = window.prompt("Why are you reporting this user? Optional details:") || "";

    setManagementBusy(true);
    setStatusMsg(null);

    try {
      await reportUser(userId, {
        originGroupId: groupId,
        reason: "user_report",
        details,
      });
      setStatusMsg("User report submitted.");
    } catch (e: any) {
      setStatusMsg(e?.message || "Could not report user.");
    } finally {
      setManagementBusy(false);
    }
  }

  async function handleSetPostingPolicy(nextPolicy: string) {
    if (!groupId) return;

    setManagementBusy(true);
    setStatusMsg(null);

    try {
      await updateGroupPostingPolicy(groupId, nextPolicy);
      setStatusMsg(`Posting policy updated: ${postingPolicyLabel(nextPolicy)}.`);
      await load();
    } catch (e: any) {
      setStatusMsg(e?.message || "Could not update posting policy.");
    } finally {
      setManagementBusy(false);
    }
  }

  async function handleSetMemberRole(userId: string | number, roleName: string) {
    if (!groupId || !userId || !roleName) return;

    const ok = window.confirm(`Change this member role to ${roleName}?`);
    if (!ok) return;

    setManagementBusy(true);
    setStatusMsg(null);

    try {
      await setGroupMemberRole(groupId, userId, roleName, "Changed from group detail UI");
      setStatusMsg("Member role updated.");
      await load();
    } catch (e: any) {
      setStatusMsg(e?.message || "Could not update member role.");
    } finally {
      setManagementBusy(false);
    }
  }

  async function handleKickMember(userId: string | number) {
    if (!groupId || !userId) return;

    const reason = window.prompt("Kick reason? Optional:") || "";
    const ok = window.confirm("Kick this member from the group?");
    if (!ok) return;

    setManagementBusy(true);
    setStatusMsg(null);

    try {
      await kickGroupMember(groupId, userId, reason);
      setStatusMsg("Member kicked.");
      await load();
    } catch (e: any) {
      setStatusMsg(e?.message || "Could not kick member.");
    } finally {
      setManagementBusy(false);
    }
  }

  async function handleBanMember(userId: string | number) {
    if (!groupId || !userId) return;

    const reason = window.prompt("Ban reason? Optional:") || "";
    const ok = window.confirm("Ban this member from the group?");
    if (!ok) return;

    setManagementBusy(true);
    setStatusMsg(null);

    try {
      await banGroupMember(groupId, userId, reason);
      setStatusMsg("Member banned.");
      await load();
    } catch (e: any) {
      setStatusMsg(e?.message || "Could not ban member.");
    } finally {
      setManagementBusy(false);
    }
  }

  async function handleToggleMyContextDm(next: boolean) {
    if (!groupId) return;

    setManagementBusy(true);
    setStatusMsg(null);

    try {
      await updateMyGroupMemberPrivacy(groupId, { allowContextDm: next });
      setStatusMsg(next ? "Group private messages enabled for you." : "Group private messages disabled for you.");
      await load();
    } catch (e: any) {
      setStatusMsg(e?.message || "Could not update your group privacy.");
    } finally {
      setManagementBusy(false);
    }
  }

  async function handleToggleGroupPrivateMessages(next: boolean) {
    if (!groupId || !item) return;

    setManagementBusy(true);
    setStatusMsg(null);

    try {
      await updateGroupPrivacy(groupId, {
        allowMemberPrivateMessages: next,
        isRecommendable: groupRecommendable,
      });
      setStatusMsg(next ? "Member private messages enabled for this group." : "Member private messages disabled for this group.");
      await load();
    } catch (e: any) {
      setStatusMsg(e?.message || "Could not update group privacy.");
    } finally {
      setManagementBusy(false);
    }
  }

  async function handleToggleRecommendable(next: boolean) {
    if (!groupId || !item) return;

    setManagementBusy(true);
    setStatusMsg(null);

    try {
      await updateGroupPrivacy(groupId, {
        allowMemberPrivateMessages: groupPrivateMessagesAllowed,
        isRecommendable: next,
      });
      setStatusMsg(next ? "Group can appear in recommendations." : "Group hidden from recommendations.");
      await load();
    } catch (e: any) {
      setStatusMsg(e?.message || "Could not update recommendation setting.");
    } finally {
      setManagementBusy(false);
    }
  }

  const currentMember = members.find((m) => String(m.user_id) === String(currentUserId)) || null;
  const myContextDmAllowed = boolish(currentMember?.allow_context_dm, true);
  const groupPrivateMessagesAllowed = boolish(item?.allow_member_private_messages, true);
  const groupRecommendable = boolish(item?.is_recommendable, true);
  const canAdminGroup = isAdminRole(viewerRole);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Group</div>
          <div className="page-subtitle">
            Review group details, members, invites, join requests, and shared todos.
          </div>
        </div>
      </div>

      <div className="card">
        <div className="section">
          <div className="section-title">Details</div>

          {loading && <p className="text-muted small">Loading…</p>}
          {error && <p className="form-error">{error}</p>}

          {!loading && !error && !item && (
            <p className="text-muted small">Group not found.</p>
          )}

          {!!item && (
            <>
              <div style={{ fontWeight: 800, fontSize: "1.05rem" }}>
                {item.label || item.name || item.group_id}
              </div>

              <p className="text-muted small" style={{ marginTop: 6 }}>
                {item.description || "No description"}
              </p>

              <div className="text-muted small" style={{ marginTop: 10 }}>
                group id: {item.group_id || groupId}
              </div>
              <div className="text-muted small">
                visibility: {item.visibility || "unknown"}
              </div>
              <div className="text-muted small">
                join policy: {item.join_policy || "unknown"}
              </div>
              <div className="text-muted small">
                discoverable: {String(item.is_discoverable ?? "")}
              </div>
              <div className="text-muted small">
                your membership: {roleLabel(viewerRole)}
              </div>
            </>
          )}
        </div>

        {!!item && (
          <div className="section">
            <div className="section-title">Safety & posting</div>

            <p className="text-muted small">
              Posting policy: {postingPolicyLabel(item?.posting_policy || "members")}
            </p>

            {String(item?.posting_policy || "members") === "voiced_only" && (
              <p className="text-muted small" style={{ marginTop: 6 }}>
                This is a voiced-only room. Only voiced members, moderators, admins, and owners can post.
              </p>
            )}

            <div className="chip-row" style={{ marginTop: 10 }}>
              <button
                className="btn-outline"
                onClick={() => void handleReportGroup()}
                disabled={managementBusy}
              >
                Report group
              </button>

              {canAdminGroup && (
                <select
                  className="form-input"
                  value={String(item?.posting_policy || "members")}
                  onChange={(e) => void handleSetPostingPolicy(e.target.value)}
                  disabled={managementBusy}
                  style={{ maxWidth: 230 }}
                >
                  <option value="members">All members can post</option>
                  <option value="voiced_only">Voiced only</option>
                  <option value="moderators_only">Moderators only</option>
                  <option value="admins_only">Admins only</option>
                  <option value="announcements">Announcements</option>
                </select>
              )}
            </div>
          </div>
        )}

        {!!item && viewerIsMember && (
          <div className="section">
            <div className="chip-row">
              <a
                className="btn-primary"
                href={`/messages?originGroupId=${encodeURIComponent(groupId)}`}
                onClick={() => {
                  try {
                    window.localStorage.setItem("intimsync.last_chat_url", `/messages?originGroupId=${encodeURIComponent(groupId)}`);
                    window.localStorage.setItem("intimsync.last_chat_label", item?.label || groupId);
                    window.localStorage.setItem("intimsync.last_chat_mode", "group");
                  } catch {}
                }}
              >
                💬 Chat
              </a>

              <button className={activeTab === "activities" ? "btn-primary" : "btn-outline"} onClick={() => setActiveTab("activities")}>
                📅 Activities
              </button>
              <button className={activeTab === "todos" ? "btn-primary" : "btn-outline"} onClick={() => setActiveTab("todos")}>
                ☑ Todos
              </button>
              <button className={activeTab === "calendar" ? "btn-primary" : "btn-outline"} onClick={() => setActiveTab("calendar")}>
                🗓 Calendar
              </button>
              <button className={activeTab === "members" ? "btn-primary" : "btn-outline"} onClick={() => setActiveTab("members")}>
                👥 Members
              </button>
              <button className={activeTab === "settings" ? "btn-primary" : "btn-outline"} onClick={() => setActiveTab("settings")}>
                ⚙ Settings
              </button>
            </div>
          </div>
        )}

        {!!item && viewerIsMember && activeTab === "members" && (
          <div className="section">
            <div className="section-title">Members</div>

            {!members.length ? (
              <p className="text-muted small">No members loaded.</p>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {members.map((m) => {
                  const userId = m.user_id || m.id || m.member_user_id;
                  const key = String(userId || m.public_id || JSON.stringify(m));
                  const isSelf = String(userId) === String(currentUserId);
                  const targetRole = String(m.role_name || "member");
                  const canModerate = !isSelf && canModerateTarget(viewerRole, targetRole);
                  const canRoleEdit = !isSelf && canChangeRoles(viewerRole) && roleRank(viewerRole) > roleRank(targetRole);

                  return (
                    <div
                      key={`${key}_${m.role_name || ""}`}
                      className="card"
                      style={{ marginBottom: 0 }}
                    >
                      <div className="section">
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                          <div>
                            <div style={{ fontWeight: 800 }}>
                              {displayMemberLabel(m)} {isSelf ? "(you)" : ""}
                            </div>

                            <p className="text-muted small" style={{ marginTop: 4 }}>
                              role: {roleLabel(m.role_name)} · private contact: {boolish(m.allow_context_dm, true) ? "on" : "off"}
                            </p>
                          </div>

                          <div className="chip-row">
                            {!isSelf && (
                              <button
                                className="btn-outline"
                                onClick={() => void handleReportUser(userId)}
                                disabled={managementBusy}
                              >
                                Report
                              </button>
                            )}

                            {canRoleEdit && (
                              <select
                                className="form-input"
                                value={targetRole}
                                onChange={(e) => void handleSetMemberRole(userId, e.target.value)}
                                disabled={managementBusy}
                                style={{ maxWidth: 150 }}
                              >
                                <option value="member">Member</option>
                                <option value="voice">Voice</option>
                                <option value="moderator">Moderator</option>
                                <option value="admin">Admin</option>
                                {viewerRole === "owner" && <option value="owner">Owner</option>}
                              </select>
                            )}

                            {canModerate && (
                              <>
                                <button
                                  className="btn-outline"
                                  onClick={() => void handleKickMember(userId)}
                                  disabled={managementBusy}
                                >
                                  Kick
                                </button>

                                <button
                                  className="btn-outline"
                                  onClick={() => void handleBanMember(userId)}
                                  disabled={managementBusy}
                                >
                                  Ban
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {!!item && viewerIsModerator && activeTab === "settings" && (
          <div className="section">
            <div className="section-title">Pending join requests</div>

            <div className="chip-row" style={{ marginBottom: 10 }}>
              <button
                className="btn-outline"
                onClick={() => void refreshJoinRequestsOnly()}
                disabled={managementBusy}
              >
                Refresh requests
              </button>
            </div>

            {!joinRequests.length ? (
              <p className="text-muted small">No pending join requests.</p>
            ) : (
              joinRequests.map((req) => {
                const requestId = req.request_id || req.id || req.join_request_id;

                return (
                  <div
                    key={requestId || JSON.stringify(req)}
                    className="card"
                    style={{ marginBottom: "0.6rem" }}
                  >
                    <div className="section">
                      <div style={{ fontWeight: 700 }}>
                        {displayRequestUser(req)}
                      </div>

                      {!!req.message && (
                        <p className="text-muted small" style={{ marginTop: 6 }}>
                          {req.message}
                        </p>
                      )}

                      <p className="text-muted small" style={{ marginTop: 6 }}>
                        status: {req.status || "pending"}
                      </p>

                      {req.status === "pending" && (
                        <div className="chip-row" style={{ marginTop: 10 }}>
                          <button
                            className="btn-primary"
                            onClick={() => void handleApproveJoinRequest(requestId)}
                            disabled={managementBusy}
                          >
                            Approve
                          </button>

                          <button
                            className="btn-outline"
                            onClick={() => void handleRejectJoinRequest(requestId)}
                            disabled={managementBusy}
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {!!item && viewerIsMember && activeTab === "activities" && (
          <div className="section">
            <div className="section-title">Activities</div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(220px, 1.2fr) minmax(220px, 1fr)",
                gap: 10,
                alignItems: "start",
              }}
            >
              <input
                className="form-input"
                placeholder="Activity title"
                value={activityTitle}
                onChange={(e) => setActivityTitle(e.target.value)}
              />

              <input
                className="form-input"
                placeholder="Optional location"
                value={activityLocation}
                onChange={(e) => setActivityLocation(e.target.value)}
              />

              <textarea
                className="form-textarea"
                rows={2}
                placeholder="Optional description"
                value={activityDescription}
                onChange={(e) => setActivityDescription(e.target.value)}
              />

              <input
                className="form-input"
                type="datetime-local"
                value={activityStartsAt}
                onChange={(e) => setActivityStartsAt(e.target.value)}
              />
            </div>

            <div className="chip-row" style={{ marginTop: 10 }}>
              <button
                className="btn-primary"
                onClick={() => void handleCreateActivity()}
                disabled={managementBusy || !activityTitle.trim()}
              >
                {managementBusy ? "Saving…" : "Create activity"}
              </button>
            </div>

            <div style={{ marginTop: 12 }}>
              {!activities.length ? (
                <p className="text-muted small">No activities yet.</p>
              ) : (
                activities.map((activity) => (
                  <div
                    key={activity.activity_id}
                    className="card"
                    style={{ marginBottom: "0.6rem" }}
                  >
                    <div className="section">
                      <div style={{ fontWeight: 700 }}>{activity.title}</div>

                      {!!activity.description && (
                        <p className="text-muted small" style={{ marginTop: 6 }}>
                          {activity.description}
                        </p>
                      )}

                      {!!activity.location && (
                        <p className="text-muted small" style={{ marginTop: 6 }}>
                          location: {activity.location}
                        </p>
                      )}

                      <p className="text-muted small" style={{ marginTop: 6 }}>
                        time: {formatActivityDate(activity.starts_at)}
                      </p>

                      <p className="text-muted small" style={{ marginTop: 6 }}>
                        status: {activityStatusLabel(activity.status)} · participants: {activity.participant_count ?? 0}
                      </p>

                      <p className="text-muted small" style={{ marginTop: 6 }}>
                        membership: {activity.viewer_joined ? "Joined" : "Not joined"}
                      </p>

                      <div className="chip-row" style={{ marginTop: 10 }}>
                        {activity.viewer_joined ? (
                          <button
                            className="btn-outline"
                            onClick={() => void handleLeaveActivity(activity.activity_id)}
                            disabled={managementBusy}
                          >
                            Leave activity
                          </button>
                        ) : (
                          <button
                            className="btn-outline"
                            onClick={() => void handleJoinActivity(activity.activity_id)}
                            disabled={managementBusy}
                          >
                            Join activity
                          </button>
                        )}

                        <a
                          className="btn-outline"
                          href={`/messages?originGroupId=${encodeURIComponent(groupId)}&originActivityId=${encodeURIComponent(activity.activity_id)}`}
                        >
                          Open activity context messages
                        </a>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {!!item && viewerIsMember && activeTab === "todos" && (
          <div className="section">
            <div className="section-title">Todos</div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(220px, 1fr) minmax(220px, 1fr)",
                gap: 10,
                alignItems: "start",
              }}
            >
              <input
                className="form-input"
                placeholder="Todo title"
                value={todoTitle}
                onChange={(e) => setTodoTitle(e.target.value)}
              />

              <input
                className="form-input"
                placeholder="Optional location"
                value={todoLocation}
                onChange={(e) => setTodoLocation(e.target.value)}
              />

              <textarea
                className="form-textarea"
                rows={2}
                placeholder="Optional description"
                value={todoDescription}
                onChange={(e) => setTodoDescription(e.target.value)}
              />

              <input
                className="form-input"
                type="datetime-local"
                value={todoDueAt}
                onChange={(e) => setTodoDueAt(e.target.value)}
              />
            </div>

            {!!members.length && (
              <div className="chip-row" style={{ marginTop: 10 }}>
                <span className="text-muted small">Assign:</span>
                {members.filter((m) => String(m.status || "active") === "active").map((m) => {
                  const id = String(m.user_id || "");
                  const selected = todoAssignedIds.includes(id);
                  return (
                    <button
                      key={`todo_assign_${id}`}
                      type="button"
                      className={selected ? "btn-primary" : "btn-outline"}
                      onClick={() => toggleTodoAssignedId(id)}
                    >
                      {memberLabel(m)}{selected ? " ✓" : ""}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="chip-row" style={{ marginTop: 10 }}>
              <button
                className="btn-primary"
                onClick={() => void handleCreateTodo()}
                disabled={managementBusy || !todoTitle.trim()}
              >
                {managementBusy ? "Saving…" : "Add todo"}
              </button>
            </div>

            <div style={{ marginTop: 12 }}>
              {!todos.length ? (
                <p className="text-muted small">No todos yet.</p>
              ) : (
                todos.map((todo) => (
                  <div
                    key={todo.todo_id || todo.id || todo.title}
                    className="card"
                    style={{ marginBottom: "0.6rem" }}
                  >
                    <div className="section">
                      <div style={{ fontWeight: 700 }}>{todo.title}</div>

                      {!!todo.description && (
                        <p className="text-muted small" style={{ marginTop: 6 }}>
                          {todo.description}
                        </p>
                      )}

                      <p className="text-muted small" style={{ marginTop: 6 }}>
                        status: {todo.status || "open"}
                      </p>

                      <p className="text-muted small" style={{ marginTop: 6 }}>
                        assigned: {todoAssigneeLabel(todo)}
                      </p>

                      {!!todo.location && (
                        <p className="text-muted small" style={{ marginTop: 6 }}>
                          location: {todo.location}
                        </p>
                      )}

                      {!!todo.due_at && (
                        <p className="text-muted small" style={{ marginTop: 6 }}>
                          due: {formatActivityDate(todo.due_at)}
                        </p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {!!item && viewerIsAdmin && activeTab === "settings" && (
          <div className="section">
            <div className="section-title">Invites</div>

            <div className="chip-row" style={{ alignItems: "flex-start" }}>
              <textarea
                className="form-textarea"
                rows={2}
                placeholder="Invite by email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                style={{ minWidth: 280 }}
              />

              <button
                className="btn-primary"
                onClick={() => void handleCreateInvite()}
                disabled={managementBusy || !inviteEmail.trim()}
              >
                {managementBusy ? "Saving…" : "Create invite"}
              </button>
            </div>

            {createdInvite?.token && (
              <div className="card" style={{ marginTop: 12, marginBottom: 12 }}>
                <div className="section">
                  <div style={{ fontWeight: 700 }}>New invite created</div>

                  <p className="text-muted small" style={{ marginTop: 6 }}>
                    Invite id: {createdInvite.inviteId || createdInvite.invite_id}
                  </p>

                  <p className="text-muted small" style={{ marginTop: 6, wordBreak: "break-all" }}>
                    Token: {createdInvite.token}
                  </p>

                  <p className="text-muted small" style={{ marginTop: 6, wordBreak: "break-all" }}>
                    Accept URL: {`${window.location.origin}/group-invites/accept?token=${encodeURIComponent(createdInvite.token)}`}
                  </p>

                  <div className="chip-row" style={{ marginTop: 10 }}>
                    <button
                      className="btn-outline"
                      onClick={() => navigator.clipboard?.writeText(`${window.location.origin}/group-invites/accept?token=${encodeURIComponent(createdInvite.token)}`)}
                    >
                      Copy invite link
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div style={{ marginTop: 12 }}>
              {!invites.length ? (
                <p className="text-muted small">No invites.</p>
              ) : (
                invites.map((inv) => (
                  <div
                    key={inv.invite_id || inv.id}
                    className="card"
                    style={{ marginBottom: "0.6rem" }}
                  >
                    <div className="section">
                      <div style={{ fontWeight: 700 }}>
                        {inv.invited_email ||
                          inv.invitedEmail ||
                          inv.invited_user_id ||
                          inv.invitedUserId ||
                          inv.invite_id}
                      </div>

                      <p className="text-muted small" style={{ marginTop: 6 }}>
                        status: {inv.status}
                      </p>

                      <p className="text-muted small">
                        invite id: {inv.invite_id}
                      </p>

                      {inv.status === "pending" && (
                        <div className="chip-row" style={{ marginTop: 10 }}>
                          <button
                            className="btn-outline"
                            onClick={() => void handleRevokeInvite(inv.invite_id)}
                            disabled={managementBusy}
                          >
                            Revoke invite
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {!!item && activeTab === "settings" && (
          <div className="section">
            <div className="section-title">Privacy & contactability</div>

            <p className="text-muted small">
              Control whether members can start private context messages from this group.
            </p>

            <div className="card" style={{ marginTop: 10, marginBottom: 10 }}>
              <div className="section">
                <div style={{ fontWeight: 700 }}>Your member privacy</div>

                <p className="text-muted small" style={{ marginTop: 6 }}>
                  Current setting: {myContextDmAllowed ? "Members can message you from this group." : "Members cannot message you from this group."}
                </p>

                <div className="chip-row" style={{ marginTop: 10 }}>
                  <button
                    className={myContextDmAllowed ? "btn-primary" : "btn-outline"}
                    onClick={() => void handleToggleMyContextDm(true)}
                    disabled={managementBusy || myContextDmAllowed}
                  >
                    Allow private contact
                  </button>

                  <button
                    className={!myContextDmAllowed ? "btn-primary" : "btn-outline"}
                    onClick={() => void handleToggleMyContextDm(false)}
                    disabled={managementBusy || !myContextDmAllowed}
                  >
                    Disable private contact
                  </button>
                </div>
              </div>
            </div>

            {canAdminGroup && (
              <div className="card" style={{ marginTop: 10 }}>
                <div className="section">
                  <div style={{ fontWeight: 700 }}>Group admin privacy</div>

                  <p className="text-muted small" style={{ marginTop: 6 }}>
                    Group private messages: {groupPrivateMessagesAllowed ? "enabled" : "disabled"} · Recommendations: {groupRecommendable ? "enabled" : "disabled"}
                  </p>

                  <div className="chip-row" style={{ marginTop: 10 }}>
                    <button
                      className={groupPrivateMessagesAllowed ? "btn-primary" : "btn-outline"}
                      onClick={() => void handleToggleGroupPrivateMessages(!groupPrivateMessagesAllowed)}
                      disabled={managementBusy}
                    >
                      {groupPrivateMessagesAllowed ? "Private member messages: on" : "Private member messages: off"}
                    </button>

                    <button
                      className={groupRecommendable ? "btn-primary" : "btn-outline"}
                      onClick={() => void handleToggleRecommendable(!groupRecommendable)}
                      disabled={managementBusy}
                    >
                      {groupRecommendable ? "Recommendations: on" : "Recommendations: off"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {!canAdminGroup && (
              <p className="text-muted small" style={{ marginTop: 10 }}>
                Group-level privacy can be changed by an owner or admin. Your role: {roleLabel(viewerRole)}.
              </p>
            )}
          </div>
        )}

        {!!item && activeTab === "settings" && (
          <div className="section">
            <div className="section-title">Access</div>

            {viewerIsMember ? (
              <>
                <p className="text-muted small">
                  You are already a member of this group.
                </p>

                <div className="chip-row" style={{ marginTop: 10 }}>
                  <a className="btn-outline" href="/groups">
                    Back to groups
                  </a>
                  <button
                    className="btn-outline"
                    onClick={() => void handleLeaveGroup()}
                    disabled={actionBusy}
                  >
                    Leave group
                  </button>

                  <a
                    className="btn-outline"
                    href={`/messages?originGroupId=${encodeURIComponent(groupId)}`}
                  onClick={() => {
                    try {
                      window.localStorage.setItem("intimsync.last_chat_url", `/messages?originGroupId=${encodeURIComponent(groupId)}`);
                      window.localStorage.setItem("intimsync.last_chat_label", item?.label || groupId);
                      window.localStorage.setItem("intimsync.last_chat_mode", "group");
                    } catch {
                      // ignore
                    }
                  }}
                  >
                    Open group chat
                  </a>
                </div>
              </>
            ) : item.join_policy === "invite_only" ? (
              <>
                <p className="text-muted small">
                  This group is invite only. Ask an owner or admin for an invitation.
                </p>

                <div className="chip-row" style={{ marginTop: 10 }}>
                  <a className="btn-outline" href="/groups">
                    Back to groups
                  </a>
                </div>
              </>
            ) : (
              <>
                <textarea
                  className="form-textarea"
                  rows={3}
                  placeholder="Optional message to the group owner or moderators"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />

                <div className="chip-row" style={{ marginTop: 10 }}>
                  <button
                    className="btn-primary"
                    onClick={() => void handleRequestJoin()}
                    disabled={actionBusy}
                  >
                    {actionBusy ? "Submitting…" : "Request to join"}
                  </button>

                  <a className="btn-outline" href="/groups">
                    Back to groups
                  </a>
                </div>
              </>
            )}

            {statusMsg && (
              <p className="text-muted small" style={{ marginTop: 10 }}>
                {statusMsg}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
